import React, { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactApexChart from 'react-apexcharts';
import './ReportingOverviewRoiOverviewPerClient.css';
import { convertCurrencyHelper } from '../../../helpers/forexConvertor';
import typeOfContract from '../../../constants/typeOfContract';

// Skeleton loader components for the donut chart
const DonutChartSkeleton = () => {
  return (
    <div style={{
      width: '200px',
      height: '235px',
      margin: '0 auto',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {/* Outer donut ring skeleton with colored segments */}
      <div style={{
        width: '193.845px',
        height: '193.845px',
        borderRadius: '50%',
        border: '30px solid',
        borderTopColor: '#8AA9D2',
        borderRightColor: '#234266',
        borderBottomColor: '#6D98CF',
        borderLeftColor: '#4263A5',
        opacity: 0.3,
        animation: 'pulse 1.5s ease-in-out infinite',
        position: 'relative',
        transform: 'rotate(-45deg)',
        boxSizing: 'border-box'
      }}>
        {/* Inner circle (donut hole) */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '133.845px',
          height: '133.845px',
          borderRadius: '50%',
          backgroundColor: '#ffffff',
        }} />
      </div>
    </div>
  );
};

const LegendSkeleton = () => {
  return (
    <div className="custom-legend">
      <div className="custom-legend-title">Top 3 Projects</div>
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="custom-legend-item">
          <div className="custom-legend-value">
            <span 
              className="custom-legend-marker" 
              style={{ 
                backgroundColor: ['#8AA9D2', '#234266', '#6D98CF'][index],
                opacity: 0.3,
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: `${index * 0.1}s`
              }}
            />
            <div 
              style={{
                width: '120px',
                height: '14px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: `${index * 0.1}s`
              }}
            />
          </div>
          <div 
            className="custom-legend-percentage"
            style={{
              width: '60px',
              height: '14px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite',
              animationDelay: `${index * 0.1}s`
            }}
          />
        </div>
      ))}
    </div>
  );
};

const useProcessedData = (trackReport, projectCost, sprints, reportingOverview, preferredCurrency, forex) => {
  return useMemo(() => {
    try {
      const currentYear = reportingOverview.year || new Date().getFullYear();

      const sprintsByProject = sprints.reduce((acc, sprint) => {
        const projectId = sprint.project?._id;
        if (projectId) {
          if (!acc[projectId]) {
            acc[projectId] = [];
          }
          acc[projectId].push(sprint);
        }
        return acc;
      }, {});

      const projectData = {};

      trackReport && trackReport.forEach(entry => {
        const project = entry.project;
        const projectId = project?._id;

        if (projectId) {
          const projectYear = new Date(entry.startTime).getFullYear();
          const preferredClientCurrency = entry?.clientId?.preferred_currency || 'USD';

          if (projectYear === currentYear && !project.is_delete && (reportingOverview.project && reportingOverview.project.includes(projectId)) && (reportingOverview.currency && reportingOverview.currency.includes(preferredClientCurrency))) {
            if (!projectData[projectId]) {
              const totalSprintBudget = (sprintsByProject[projectId] || []).reduce((sum, sprint) => {
                return sum + convertCurrencyHelper(preferredClientCurrency, preferredCurrency, sprint.budget, forex);
              }, 0);

              projectData[projectId] = {
                name: project.project_name,
                budget: totalSprintBudget,
                cost: 0
              };
            }

            // const userCost = entry.user.cost || 0;
            const projectSpecificCost = projectCost && projectCost.find(cost => cost.userId === entry.user?._id && cost.projectId === projectId && !cost.is_delete);
            const paymentCurrency = entry?.user?.payment_currency || 'USD';
            // const userCost = projectSpecificCost && projectSpecificCost.amount > 0 ? projectSpecificCost.amount : (entry.user.cost || 0);
            let userCost;
            if (projectSpecificCost && projectSpecificCost.amount > 0) {
              userCost = convertCurrencyHelper(
                preferredClientCurrency,
                paymentCurrency,
                projectSpecificCost.amount,
                forex
              );
            } else {
              userCost = entry?.user?.cost || 0;
            }
            const userTypeOfContract = entry.user && entry.user?.typeOfContract || typeOfContract.Hourly_Rate;
            const userOverheadCost = entry.user && entry.user?.overhead_cost || 0;

            let finalCost;
            if (userTypeOfContract === typeOfContract.Hourly_Rate) {
              finalCost = userCost;
            } else {
              finalCost = userCost / parseInt(entry?.user?.monthly_hours ? entry?.user?.monthly_hours : 168); // Assuming a different type of contract with monthly rate divided by 168 hours
            }
            const cost = (finalCost * (entry.duration / 3600)) + (userOverheadCost * (entry.duration / 3600));
            const costConverted = convertCurrencyHelper(paymentCurrency, preferredCurrency, cost, forex);

            projectData[projectId].cost += costConverted;
          }
        }
      });

      return Object.values(projectData).map(project => ({
        name: project.name || 'Unknown',
        roi: project.cost ? ((project.budget - project.cost) / project.cost * 100).toFixed(2) : 0
      }));
    } catch (error) {
      console.error('Error processing ROI data:', error);
      return [];
    }
  }, [trackReport, projectCost, sprints, reportingOverview, preferredCurrency, forex]);
};


function RoiOverviewPerProject({ trackReport, projectCost, sprints, trackReportLoading, projectCostLoading, sprintLoading }) {
  const reportingOverview = useSelector((state) => state.reportingOverview);
  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));

  const isLoading = trackReportLoading || projectCostLoading || sprintLoading;

  const processedData = useProcessedData(
    trackReport, 
    projectCost, 
    sprints, 
    reportingOverview, 
    settings?.currency || 'USD', 
    forex
  );

  const series = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];
    return processedData.map(project => parseFloat(project.roi) || 0);
  }, [processedData]);

  const labels = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];
    return processedData.map(project => project.name || 'Unknown');
  }, [processedData]);
  // Colors array for all clients
  const allColors = [
    '#8AA9D2', '#234266', '#6D98CF', '#4263A5',
    '#6BA9E2', '#9ACBFB', '#75AECF', '#3f4b57',
    '#6A9DCB', '#A4C4E6', '#9AC9F3', '#000',
    '#89A7C4', '#8EA9C6', '#BBCFE7', '#B7D4F0',
    '#A6C0D6', '#4B6F98', '#A2B5CB', '#86A0BC'
  ];

  // Function to get top 3 series and corresponding labels and colors
  const getTop3Series = () => {
    if (!series || series.length === 0 || !labels || labels.length === 0) {
      return { top3Labels: [], top3Colors: [] };
    }

    try {
      // Create an array of objects { value, index } to keep track of original indices
      const seriesWithIndices = series.map((value, index) => ({ value: parseFloat(value) || 0, index }));

      // Sort by value in descending order
      seriesWithIndices.sort((a, b) => b.value - a.value);

      // Extract top 3 series values and labels
      const top3Series = seriesWithIndices.slice(0, 3);
      const top3Labels = top3Series.map(item => labels[item.index] || 'Unknown');
      const top3Colors = top3Series.map(item => allColors[item.index % allColors.length]);

      return { top3Labels, top3Colors };
    } catch (error) {
      console.error('Error getting top 3 series:', error);
      return { top3Labels: [], top3Colors: [] };
    }
  };

  const { top3Labels, top3Colors } = getTop3Series();

  const options = {
    chart: {
      width: 193.845,
      height: 193.845,
      type: 'donut',
    },
    plotOptions: {
      pie: {
        startAngle: -45,
        endAngle: 315,
        expandOnClick: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'gradient',
    },
    legend: {
      show: false, // Hide the default legend
    },
    labels: labels, // All clients
    tooltip: {
      enabled: true,
      y: {
        formatter: function (val) {
          return val + '%';
        },
      },
      custom: function ({ series, seriesIndex, w }) {
        const project = labels[seriesIndex];
        const roiValue = series[seriesIndex];
        const totalROI = series.reduce((total, val) => total + val, 0);
        const percentage = ((roiValue / totalROI) * 100).toFixed(2);
        return `<div class="apexcharts-tooltip-custom" style="background: #FFFFFF; box-shadow: 0px 1px 8px rgba(110, 110, 110, 0.1); border-radius: 0px;height: 44pxxpx;padding: 5px 10px;">
                  <span class="apexcharts-tooltip-marker"></span>
                  <span class="apexcharts-tooltip-text" style="font-weight: 300; font-size: 11px; line-height: 13.42px; letter-spacing: 0.5px; color: #282828;">${project}</span>
                  <span class="apexcharts-tooltip-value" style="font-weight: 300; font-size: 11px; line-height: 13.42px; letter-spacing: 0.5px; color: #282828;">${roiValue} - ${percentage}%</span>
                </div>`;
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
    colors: allColors, // Colors for each segment (all clients)
    states: {
      active: {
        filter: {
          type: "none", // No visual change for active state
        },
      },
      normal: {
        filter: {
          type: "none", // No visual change for normal state
        },
      },
    },
  };

  const allNegative = useMemo(() => {
    if (!series || series.length === 0) return false;
    return series.every(value => parseFloat(value) <= 0);
  }, [series]);

  const [showMessage, setShowMessage] = useState(false);
  
  useEffect(() => {
    if (allNegative && !isLoading) {
      // Set a delay of 2 seconds before showing the message
      const timer = setTimeout(() => {
        setShowMessage(true);
      }, 2000);

      // Clear the timer if component unmounts
      return () => clearTimeout(timer);
    } else {
      setShowMessage(false);
    }
  }, [allNegative, isLoading]);

  // Custom Legend Component
  const CustomLegend = () => {
    if (!top3Labels || top3Labels.length === 0) {
      return (
        <div className='custom-legend'>
          <div className='custom-legend-title'>Top 3 Projects</div>
        </div>
      );
    }

    return (
      <div className='custom-legend'>
        <div className='custom-legend-title'>Top 3 Projects</div>
        {top3Labels.map((label, index) => {
          const labelIndex = labels.indexOf(label);
          const value = labelIndex >= 0 && series[labelIndex] !== undefined 
            ? parseFloat(series[labelIndex]).toFixed(2) 
            : '0.00';
          
          return (
            <div key={`${label}-${index}`} className='custom-legend-item'>
              <div className='custom-legend-value'>
                <span className='custom-legend-marker' style={{ backgroundColor: top3Colors[index] || '#8AA9D2' }}></span>
                <span className='custom-legend-text'>{label}</span>
              </div>
              <div className='custom-legend-percentage'>{value}%</div>
            </div>
          );
        })}
      </div>
    );
  };

  // Show skeleton loader while loading
  if (isLoading) {
    return (
      <>
        <div className='outer-piechart-report'>
          <div className='outer-piechart-title outer-piechart-title-desktop'>
            ROI Overview per Project
          </div>
          <div className='outer-piechart-section' style={{ display: 'flex', flexDirection: 'column', minHeight: '235px' }}>
            <div className='outer-piechart-title outer-piechart-title-mobile'>
              ROI Overview per Project
            </div>
            <DonutChartSkeleton />
          </div>
          <LegendSkeleton />
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </>
    );
  }

  // Show error or empty state
  if (!processedData || processedData.length === 0) {
    return (
      <div className='outer-piechart-report'>
        <div className='outer-piechart-title outer-piechart-title-desktop'>
          ROI Overview per Project
        </div>
        <div className='outer-piechart-section' style={{ display: 'flex', flexDirection: 'column', minHeight: '235px' }}>
          <div className='outer-piechart-title outer-piechart-title-mobile'>
            ROI Overview per Project
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: '235px' }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px" }}>
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="10" width="32" height="26" rx="3" stroke="#D0D5DD" strokeWidth="2" />
                <path d="M6 16h32" stroke="#D0D5DD" strokeWidth="2" />
                <path d="M14 7v6M30 7v6" stroke="#D0D5DD" strokeWidth="2" strokeLinecap="round" />
                <path d="M13 24h6M13 29h12" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <p style={{ margin: 0, fontSize: "15px", fontWeight: "500", color: "#282828" }}>No data to display</p>
            </div>
          </div>
        </div>
        <div className="custom-legend">
          <div className="custom-legend-title">Top 3 Projects</div>
        </div>
      </div>
    );
  }

  return (
    <div className='outer-piechart-report'>
      <div className='outer-piechart-title outer-piechart-title-desktop'>
        ROI Overview per Project
      </div>
      <div className='outer-piechart-section' style={{ display: 'flex', flexDirection: 'column', minHeight: '235px' }}>
        <div className='outer-piechart-title outer-piechart-title-mobile'>
          ROI Overview per Project
        </div>
        {allNegative && showMessage ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: '235px' }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px" }}>
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="10" width="32" height="26" rx="3" stroke="#D0D5DD" strokeWidth="2" />
                <path d="M6 16h32" stroke="#D0D5DD" strokeWidth="2" />
                <path d="M14 7v6M30 7v6" stroke="#D0D5DD" strokeWidth="2" strokeLinecap="round" />
                <path d="M13 24h6M13 29h12" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <p style={{ margin: 0, fontSize: "15px", fontWeight: "500", color: "#282828" }}>No data to display</p>
            </div>
          </div>
        ) : (
          <ReactApexChart
            options={options}
            series={series}
            type='donut'
            width={200}
            height={235}
          />
        )}
      </div>
      <CustomLegend />
    </div>
  );
}

export default RoiOverviewPerProject;
